/**
 * Interface for the 'Quotes' data
 */

import { Endorsement } from './endorsement.model';
import { includes, values } from 'lodash';
import { Proposal } from './proposal';
import { QuoteWorkflowStatus } from './quote-workflow-status';
import { QUOTE_STATUSES, QuoteStatus } from './quote-status';
import { QuotePermissions } from './quote-permissions';

type Nulled<T> = { [K in keyof T]: T[K] | null };

export const QUOTE_TYPES = {
  Renewal: 'renewal',
  MTA: 'mid-term-adjustment',
  NewBusiness: 'new-business',
} as const;

export type QuoteType = (typeof QUOTE_TYPES)[keyof typeof QUOTE_TYPES];

export const PAYMENT_TYPE = {
  None: '',
  Offline: 'offline',
  Invoice: 'invoice',
  PremiumFinance: 'premium-finance',
  Stripe: 'stripe',
} as const;

export type PaymentType = (typeof PAYMENT_TYPE)[keyof typeof PAYMENT_TYPE];

export function isPaymentType(value: unknown): value is PaymentType {
  return includes(values(PAYMENT_TYPE), value);
}

interface QuoteBase {
  id: number;
  locktonPolicyId: number;
  portalId: number;
  clientId: number;
  clientName: string;
  description: string;
  createdDate: string;
  updatedDate: string | null;
  propFormId: string;
  hasSpecialRequirements: boolean;
  leadInsurer: string;
  paymentType: PaymentType;
  policyStartDate: string;
  policyEndDate: string;
  policyRenewalDate: string;
  assignedUserId: string | null;
  assignedUserName: string | null;
  assignedContactUsers: { userId: string; displayName: string }[];
  classOfBusinessId: number;
  fee: number;
  legalFee: number;
  type: QuoteType;
  relatesToAddonId: number | null; // this is currently only available for LSOS all others will be null
  replacesQuoteId: number;
  adjustment: QuoteAdjustment;
  addons?: QuoteAddon[];
  statusHistory: QuoteStatusHistory[];
  workflowStatus: QuoteWorkflowStatus;
  checkList: Record<string, boolean>;
  proposal: Proposal | null;
  permissions?: QuotePermissions;
  additionalFields: Record<string, string>;
}

export interface QuoteStatusHistory {
  id: number;
  quoteId: number;
  status: QuoteStatus;
  workflowStatus: QuoteWorkflowStatus;
  timestamp: string;
}

export interface QuoteAdjustment {
  amount: number;
  percentage: number;
}

export interface QuoteAddon {
  id: number;
  quoteId: number;
  classOfBusinessId: number;
  ipt: number;
  brokerFee: number;
  netPremium: number;
  grossPremium: number;
  enabled: boolean;
  limitOfIndemnity: number;
}

export interface QuoteOption {
  quoteId: number;
  portalId: number;
  optionId: number;
  selected: boolean;
  additionalFields: Record<string, any>;
}

type QuoteMonetaryValues = {
  premium: number;
  ipt: number;
  brokerFee: number | null;
  indemnityLimit: number;
  voluntaryExcess: number;
  maximumAggregateSia: number;
  basicPremium: number;
  criminalCourtDiscount: number;
  lowFeeDiscount: number;
  principalNumberDiscount: number;
  feeIncomeLoading: number;
  workTypeLoading: number;
  claimDiscountLoading: number;
  siaOption: number;
  selfInsuredAmountTotal: number;
  totalPremium: number;
  daysOnCover: number;
  periodPremium: number;
  totalCalculatedSia: number;
  originalQuotePremiums: {
    brokerFee: number;
    grossPremium: number;
    netPremium: number;
  };
};

export interface QuoteUnquoted extends QuoteBase, Nulled<QuoteMonetaryValues> {
  policyId: null;
  status:
    | typeof QUOTE_STATUSES.Initialized
    | typeof QUOTE_STATUSES.FormInitialized
    | typeof QUOTE_STATUSES.FormIncomplete
    | typeof QUOTE_STATUSES.FormComplete
    | typeof QUOTE_STATUSES.OnHold
    | typeof QUOTE_STATUSES.HealthEvaluationRequired
    | typeof QUOTE_STATUSES.Defunct
    | typeof QUOTE_STATUSES.NotTakenUp;
}

export interface QuoteQuoted extends QuoteBase, QuoteMonetaryValues {
  policyId: null;
  status:
    | typeof QUOTE_STATUSES.HealthEvaluationActive
    | typeof QUOTE_STATUSES.Quoted
    | typeof QUOTE_STATUSES.BrokerApproved
    | typeof QUOTE_STATUSES.OnHold
    | typeof QUOTE_STATUSES.NotTakenUp;
}

export interface QuoteAwaiting extends QuoteBase, QuoteMonetaryValues {
  policyId: number;
  status: typeof QUOTE_STATUSES.AwaitingPayment;
}

export interface QuoteComplete extends QuoteBase, QuoteMonetaryValues {
  policyId: number;
  status: typeof QUOTE_STATUSES.Complete;
}

export type Quote = QuoteUnquoted | QuoteQuoted | QuoteAwaiting | QuoteComplete;

export function isQuoteUnquoted(quote: Quote): quote is QuoteUnquoted {
  return (
    quote.status === QUOTE_STATUSES.FormInitialized ||
    quote.status === QUOTE_STATUSES.FormIncomplete ||
    quote.status === QUOTE_STATUSES.OnHold ||
    quote.status === QUOTE_STATUSES.FormComplete ||
    quote.status === QUOTE_STATUSES.HealthEvaluationRequired
  );
}

export function isQuoteQuoted(quote: Quote): quote is QuoteQuoted {
  return (
    quote.status === QUOTE_STATUSES.HealthEvaluationActive ||
    quote.status === QUOTE_STATUSES.Quoted ||
    quote.status === QUOTE_STATUSES.BrokerApproved ||
    quote.status === QUOTE_STATUSES.AwaitingPayment
  );
}

export function isQuoteComplete(quote: Quote): quote is QuoteComplete {
  return quote.status === QUOTE_STATUSES.Complete;
}

export const QuoteGuards = {
  isQuoteUnquoted,
  isQuoteQuoted,
  isQuoteComplete,
};

export interface QuoteBrokerAssignment {
  quoteId: number;
  userId: string;
}

export interface QuoteStatusUpdate {
  portalId: number;
  quoteId: number;
  status: QuoteStatus;
  additionalData?: Endorsement;
}

export interface QuoteTier {
  icon: string;
  label: string;
  options: QuoteTierOptions;
}

export interface QuoteTierOptions {
  id: number;
  selected: boolean;
  loi: QuoteTierOption;
  excess: QuoteTierOption;
}

export interface QuoteTierOption {
  options: number[];
  selected?: number;
}

export interface QuoteAddonOptions {
  loi: number[];
}

export interface QuoteAddonFields {
  enabled: boolean;
  limitOfIndemnity: number;
}

export interface QuoteAddonUpdate {
  quoteId: number;
  classOfBusinessId: number;
  productData: Partial<QuoteAddonFields>;
}

export interface QuoteTierFields {
  excess: number;
  limitOfIndemnity: number;
}

export interface QuoteTierUpdate {
  quoteId: number;
  optionId: number;
  selected: boolean;
  portalId: number;
  clientId: number;
  classOfBusinessId: number;
  additionalFields: QuoteTierFields;
}

export interface QuoteTierRateFields {
  excess: number;
  limitOfIndemnity: number;
}

export interface QuoteTierRateUpdate {
  quoteId: number;
  classOfBusinessId: number;
  productData: QuoteTierRateFields;
}

export interface QuoteTierRate {
  brokerFeeAmount: number;
  grossPremium: number;
  ipt: number;
  netPremium: number;
  rateCode: number;
  meta?: QuoteTierRateMeta;
}

interface QuoteTierRateMeta extends QuoteTierRateFields {
  tier: number;
}

export type QuoteTierRates = Record<string, QuoteTierRate>;

export const QUOTE_RESPONSE_ACTIONS = {
  RedirectFormSuccess: 'redirect-form-success',
  RedirectFormReferred: 'redirect-form-referred',
  RedirectEnquiryOverview: 'redirect-enquiry-overview',
  RedirectEnquiryConfiguration: 'redirect-enquiry-configuration',
  RedirectQuoteOptions: 'redirect-quote-options',
  RedirectQuoteSummary: 'redirect-quote-summary',
  NotificationEmailSent: 'notification-email-sent',
  RedirectFormReferredDueToLateSubmission:
    'redirect-form-referred-due-to-late-submission',
} as const;

export interface QuoteResponseAction {
  type: (typeof QUOTE_RESPONSE_ACTIONS)[keyof typeof QUOTE_RESPONSE_ACTIONS];
}

export type PatchQuoteResponse = {
  responses: Array<QuoteResponseAction>;
  quote: Quote;
};

export interface QuoteAPIPermissions {
  canSendRenewalInvite: boolean;
  canUpdateAdjustment: boolean;
  canUpdateEndorsements: boolean;
  canUpdateAddons: boolean;
  canUpdateOptions: boolean;
  canSendQuote: boolean;
  canAcceptQuote: boolean;
  canPay: boolean;
  canReturnProposal: boolean;
  canSubmitProposal: boolean;
  canUpdateProposal: boolean;
  canPayByPremiumFinance: boolean;

  // deprecated permissions
  isHidden: boolean;
  isProposalReadonly: boolean;
  isQuoteReadonly: boolean;
}

export type QuoteLite = Pick<
  Quote,
  | 'id'
  | 'portalId'
  | 'clientId'
  | 'clientName'
  | 'policyId'
  | 'classOfBusinessId'
  | 'description'
  | 'createdDate'
  | 'updatedDate'
  | 'propFormId'
  | 'policyStartDate'
  | 'policyEndDate'
  | 'policyRenewalDate'
  | 'assignedUserId'
  | 'assignedUserName'
  | 'addons'
  | 'type'
  | 'status'
  | 'workflowStatus'
  | 'additionalFields'
  | 'permissions'
>;
